import { fetchAuthSession } from 'aws-amplify/auth';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { graphqlClient } from '../../services/graphqlClient';
import { localS3Overrides } from '../../services/localS3';
import { buildSubmitProcessingPayload } from '../../services/apiPayloadBuilder';
import * as mutations from '../../graphql/mutations';
import { generateClient } from 'aws-amplify/api';
import { JobType } from '../../graphql/types';
import type { Loan, WorkflowJob } from '../../graphql/types';
import * as analyzeData from '../analyze/analyzeData';
import { buildTape, parseUploadLocation } from './intakeDDModel';
import type { DraftLoan, Tape } from './intakeDDModel';

/**
 * Data calls for DD Intake. They reuse the services the legacy
 * `DocumentProcessingStep` uses (loans through `graphqlClient`, files straight
 * to S3, then `submitDocumentsForProcessing` + `submitJob`), so the backend
 * sees the same requests as before.
 */

/** Largest tape accepted. Tapes are read in the browser. */
export const MAX_TAPE_BYTES = 10 * 1024 * 1024;

interface Caller {
  tenantId: string;
  username: string;
  cognitoSub: string;
}

async function getCaller(): Promise<Caller> {
  const session = await fetchAuthSession();
  const payload = session.tokens?.idToken?.payload ?? {};
  return {
    tenantId: (payload['custom:tenantId'] as string) || 'default-tenant',
    username: (payload['cognito:username'] as string) || 'unknown',
    cognitoSub: (payload['sub'] as string) || 'unknown',
  };
}

/** Read an XLSX or CSV file into a Tape. Throws a user-readable Error. */
export async function parseTapeFile(file: File): Promise<Tape> {
  if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
    throw new Error('Choose an XLSX or CSV file.');
  }
  if (file.size > MAX_TAPE_BYTES) {
    throw new Error('That file is larger than 10 MB. Split the tape and upload it in parts.');
  }
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const matrix = sheet
    ? (XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' }) as unknown[][])
    : [];
  const tape = buildTape(file.name, matrix);
  if (!tape) {
    throw new Error('No loans were found in that file. Check that it has a header row and at least one loan.');
  }
  return tape;
}

export async function loadLoans(jobId: string): Promise<Loan[]> {
  const { tenantId } = await getCaller();
  const result = await graphqlClient.queries.listLoansByJob(jobId, tenantId);
  return result.items;
}

export interface CreatedLoan {
  rowIndex: number;
  loan: Loan;
}

/** Create one loan per draft, in order. Stops at the first failure. */
export async function createLoans(
  jobId: string,
  drafts: ReadonlyArray<DraftLoan>,
  onCreated?: (created: CreatedLoan) => void
): Promise<CreatedLoan[]> {
  const caller = await getCaller();
  const created: CreatedLoan[] = [];
  for (const draft of drafts) {
    const loan = await graphqlClient.mutations.createLoan({
      loanId: `loan-${Date.now()}-${draft.rowIndex}`,
      jobId,
      ...caller,
      loanNumber: draft.loanNumber,
      borrowerName: draft.borrowerName || undefined,
      propertyAddress: draft.propertyAddress || undefined,
      loanAmount: draft.loanAmount || undefined,
    });
    const item = { rowIndex: draft.rowIndex, loan };
    created.push(item);
    onCreated?.(item);
  }
  return created;
}

/** Write a re-mapped tape row onto its existing loan. */
export async function updateLoan(jobId: string, loanId: string, draft: DraftLoan): Promise<void> {
  const { tenantId } = await getCaller();
  await graphqlClient.mutations.updateLoanInfo(
    loanId,
    jobId,
    tenantId,
    draft.loanNumber,
    draft.borrowerName,
    draft.propertyAddress,
    draft.loanAmount
  );
}

/** Put one file in the job's upload location, under the loan's folder. */
export async function uploadLoanFile(job: WorkflowJob, loanId: string, file: File): Promise<void> {
  const session = await fetchAuthSession();
  const creds = session.credentials;
  if (!creds) throw new Error('No credentials available');

  const location = parseUploadLocation(job.uploadLocation);
  const bucket = location?.bucket || import.meta.env.VITE_S3_DOCUMENTS_BUCKET;
  if (!bucket) throw new Error('Upload bucket not configured');
  const prefix = location ? location.prefix : `default-tenant/${job.username}/${job.jobId}/`;

  const s3 = new S3Client({
    region: import.meta.env.VITE_AWS_REGION || 'us-west-2',
    credentials: {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
    },
    requestChecksumCalculation: 'WHEN_REQUIRED',
    responseChecksumValidation: 'WHEN_REQUIRED',
    ...localS3Overrides(),
  });
  await s3.send(
    new PutObjectCommand({
      Bucket: bucket,
      Key: `${prefix}${loanId}/${file.name}`,
      Body: new Uint8Array(await file.arrayBuffer()),
      ContentType: file.type,
    })
  );
}

/** What Intake needs to show "x of y docs" for each loan. */
export interface RequiredDocsInfo {
  /** Required document types of the chosen or default guideline. */
  required: string[];
  /** Types of the documents already extracted, per loan id. */
  typesByLoan: Record<string, string[]>;
}

/**
 * The required document types and the document types each loan already has.
 *
 * The guideline is the one chosen in Analyze for this job, else the only
 * guideline the tenant has. The guideline list does not carry its required
 * documents, so the set comes from the stored completeness check for that
 * guideline (one result per required document). Returns null when there is no
 * guideline to apply or the check has not run yet; Intake then shows the file
 * count alone. Never throws: this is a hint, not a gate.
 */
export async function loadRequiredDocs(jobId: string): Promise<RequiredDocsInfo | null> {
  try {
    const ctx = await analyzeData.loadAnalyzeContext(jobId);
    let guidelineId: string | null = null;
    try {
      guidelineId = window.localStorage.getItem(`lastGuideline_${jobId}`);
    } catch {
      guidelineId = null;
    }
    if (!guidelineId) {
      const guidelines = await analyzeData.loadGuidelines(ctx.tenantId, JobType.DD);
      guidelineId = guidelines.length === 1 ? guidelines[0].guidelineId : null;
    }
    if (!guidelineId) return null;

    const stored = await analyzeData.loadStoredAnalysis(jobId, ctx.tenantId, guidelineId);
    const required = Object.keys(stored.completeness ?? {});
    if (required.length === 0) return null;

    const typesByLoan: Record<string, string[]> = {};
    for (const doc of ctx.documents) {
      if (doc.documentType) (typesByLoan[doc.loanId] ??= []).push(doc.documentType);
    }
    return { required, typesByLoan };
  } catch (err) {
    console.warn('[IntakeDD] Required documents not available:', err);
    return null;
  }
}

/** Send the uploaded files to extraction. `isResubmission` when a run already started. */
export async function submitForExtraction(
  job: WorkflowJob,
  jobType: JobType,
  isResubmission: boolean
): Promise<void> {
  const { tenantId } = await getCaller();
  const client = generateClient();
  await client.graphql({
    query: mutations.submitDocumentsForProcessing,
    variables: { input: buildSubmitProcessingPayload(job.jobId, tenantId, jobType) },
  });
  await client.graphql({
    query: mutations.submitJob,
    variables: { input: isResubmission ? { jobId: job.jobId, isResubmission: true } : { jobId: job.jobId } },
  });
}
